import { useLayoutEffect, useRef } from "react";
import { Bell, CloudSun, LayoutDashboard, LineChart } from "lucide-react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useVirtualKeyboard } from "@/hooks/useVirtualKeyboard";
import { prefetchRoute } from "@/lib/routeLoaders";

const items = [
  { to: "/", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/climate", label: "Clima", icon: CloudSun },
  { to: "/analysis", label: "Análisis", icon: LineChart },
  { to: "/alerts", label: "Alertas", icon: Bell },
];

export function MobileBottomNav() {
  const navRef = useRef<HTMLElement>(null);
  // Con el teclado abierto la barra queda flotando justo encima de él y roba
  // la mitad del hueco que queda para ver lo que se escribe.
  const isKeyboardOpen = useVirtualKeyboard();

  // La barra publica su altura REAL (incluida la safe-area del dispositivo) en
  // --bottom-nav-h. Antes el contenido reservaba el hueco con un pb-24 escrito
  // a mano: cambiar la altura de la barra tapaba el final de cada página.
  useLayoutEffect(() => {
    const element = navRef.current;
    if (!element) return;

    const root = document.documentElement;
    const publishHeight = () => {
      // Oculta ⇒ no reserva hueco: si no, quedaría una franja muerta bajo el
      // contenido mientras se escribe.
      const height = isKeyboardOpen ? 0 : element.offsetHeight;
      root.style.setProperty("--bottom-nav-h", `${height}px`);
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(element);

    return () => {
      observer.disconnect();
      root.style.removeProperty("--bottom-nav-h");
    };
  }, [isKeyboardOpen]);

  return (
    <nav
      ref={navRef}
      aria-label="Navegación principal móvil"
      aria-hidden={isKeyboardOpen || undefined}
      // `translate-y-full` en vez de `hidden`: la barra vuelve deslizándose en
      // lugar de aparecer de golpe al cerrarse el teclado.
      className={cn(
        "fixed inset-x-0 bottom-0 z-nav border-t border-border bg-background/95 px-safe pb-safe backdrop-blur-xl md:hidden",
        "transition-transform duration-moderate ease-smooth",
        isKeyboardOpen && "pointer-events-none translate-y-full",
      )}
    >
      <div className="grid h-16 grid-cols-4">
        {items.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onTouchStart={() => prefetchRoute(to)}
            onPointerEnter={() => prefetchRoute(to)}
            onFocus={() => prefetchRoute(to)}
            // `touch-manipulation` quita el retardo de 300ms que algunos
            // navegadores móviles guardan por si el toque era un doble tap.
            className={({ isActive }) =>
              cn(
                "flex min-w-0 touch-manipulation flex-col items-center justify-center gap-1 px-1 text-2xs font-medium",
                "transition-colors duration-fast ease-smooth",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                isActive ? "text-primary" : "text-muted-foreground",
              )
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={cn(
                    "flex h-7 min-w-12 items-center justify-center rounded-full",
                    "transition-[background-color,transform] duration-fast ease-smooth",
                    isActive && "bg-primary/10",
                  )}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="truncate">{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
